/// <amd-module name="Build/Stats/ReleaseMetricsSection" />

import * as React from "react";

import { IVssContributionService } from "VSS/Platform/Contribution";
import { Components, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";

import { PipelinesMetricsContainer, PipelinesMetricsPayload } from "Build/Stats/PipelinesMetricsContainer";
import * as Resources from "Build/Stats/Resources";

interface DeploymentMerticsData {
    deploymentMetrics: {
        name: string;
        value: number;
    }[];
}

const enum DefinitionMetrics {
    SuccessfulDeployments = "SuccessfulDeployments",
    FailedDeployments = "FailedDeployments",
    PartiallySuccessfulDeployments = "PartiallySuccessfulDeployments"
}

const enum ReleasePerfConstants {
    Area = "ReleaseMetricsSection",
    Scenario = "FetchReleaseMetrics"
}

interface ReleaseMetricsSectionProps extends IVssComponentProperties {
    numOfDays: number;
}

class ReleaseMetricsSection extends VssComponent<ReleaseMetricsSectionProps, {}> {
    public render(): JSX.Element | null {
        return (
            <PipelinesMetricsContainer
                succeededText={Resources.DeploymentsSucceeded}
                fetchMetrics={this.fetchMetrics}
                numOfDays={this.props.numOfDays}
            />
        );
    }

    public fetchMetrics = (numOfDays: number): Promise<PipelinesMetricsPayload | null> => {
        const PROVIDER_ID = "ms.vss-releaseManagement-web.metrics-data-provider";
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");

        const date = new Date();
        let passed: number = 0;
        let failed: number = 0;

        if (numOfDays === 1) {
            date.setDate(date.getDate() - numOfDays);
        } else {
            // since build supports day precision for 7,30 days range
            // make RM metrics behave similarly
            date.setUTCDate(date.getUTCDate() - numOfDays);
            // UTC midnight
            date.setUTCHours(0, 0, 0, 0);
        }
        const properties = {
            minmetricstime: date,
            operation: "getdeploymentmetrics"
        };

        performanceService.startScenario(ReleasePerfConstants.Scenario, false);
        return this.context.pageContext
            .getService<IVssContributionService>("IVssContributionService")
            .getDataAsync<DeploymentMerticsData>(PROVIDER_ID, properties)
            .then(
                (data: DeploymentMerticsData) => {
                    if (data) {
                        performanceService.endScenario(ReleasePerfConstants.Area, ReleasePerfConstants.Scenario, false);
                        for (let metric of data.deploymentMetrics) {
                            if (
                                metric.name === DefinitionMetrics.SuccessfulDeployments ||
                                metric.name === DefinitionMetrics.PartiallySuccessfulDeployments
                            ) {
                                passed += metric.value;
                            } else if (metric.name === DefinitionMetrics.FailedDeployments) {
                                failed = metric.value;
                            }
                        }

                        return {
                            passed: passed,
                            failed: failed
                        };
                    } else {
                        this._endPerfScenario(performanceService);
                        return null;
                    }
                },
                () => {
                    this._endPerfScenario(performanceService);
                    return null;
                }
            );
    };

    private _endPerfScenario(performanceService: IVssPerformanceService): void {
        performanceService.endScenario(ReleasePerfConstants.Area, ReleasePerfConstants.Scenario, false, undefined, { status: "failed" });
    }
}

Components.add("Pipelines.ReleasesStats", ReleaseMetricsSection);
